import { ReactNode } from 'react';

import { GetActionTypes, createAction } from 'utils/reducer/actions';
import { IconType } from 'utils/types';

export type NotificationVariant = 'primary' | 'alert';

enum NotificationActionEnum {
  ADD = 'add',
  EDIT = 'edit',
  REMOVE = 'remove',
  REMOVE_BY_PREFIX = 'removeByPrefix',
  MARK_AS_READ = 'markAsRead',
  MARK_ALL_AS_READ = 'markAllAsRead',
  CLEAR_ALL = 'clearAll',
}

export interface Notification {
  id: string;
  icon: IconType;
  content: ReactNode;
  timestamp: number;
  isRead: boolean;
  onRead?: () => Promise<void | unknown> | void;
  onClick: () => void;
  title?: ReactNode;
  variant?: NotificationVariant;
}

export const notificationActions = {
  addNotification: (options: Notification) => createAction(NotificationActionEnum.ADD, { ...options }),
  editNotification: (id: string, options: Partial<Omit<Notification, 'id'>>) =>
    createAction(NotificationActionEnum.EDIT, { id, ...options }),
  removeNotification: (id: string) => createAction(NotificationActionEnum.REMOVE, id),
  removeNotificationsByPrefix: (prefix: string) => createAction(NotificationActionEnum.REMOVE_BY_PREFIX, prefix),
  markAsRead: (id: string) => createAction(NotificationActionEnum.MARK_AS_READ, id),
  markAllAsRead: () => createAction(NotificationActionEnum.MARK_ALL_AS_READ, {}),
  clearAllNotifications: () => createAction(NotificationActionEnum.CLEAR_ALL, {}),
};

export const notificationsReducer = (
  state: Notification[],
  { payload, type }: GetActionTypes<typeof notificationActions>
): Notification[] => {
  switch (type) {
    case NotificationActionEnum.ADD: {
      const exists = state.some((notification) => notification.id === (payload as Notification).id);
      if (exists) return state;

      return state.concat([{ ...(payload as Notification) }]).sort((a, b) => b.timestamp - a.timestamp);
    }
    case NotificationActionEnum.EDIT:
      return state
        .map((notification) => (notification.id === payload.id ? { ...notification, ...payload } : notification))
        .sort((a, b) => b.timestamp - a.timestamp);
    case NotificationActionEnum.REMOVE:
      return state.filter((notification) => notification.id !== payload);
    case NotificationActionEnum.REMOVE_BY_PREFIX:
      return state.filter((notification) => !notification.id.startsWith(payload as string));
    case NotificationActionEnum.MARK_AS_READ:
      return state.map((notification) =>
        notification.id === payload ? { ...notification, isRead: true } : notification
      );
    case NotificationActionEnum.MARK_ALL_AS_READ:
      return state.map((notification) => ({ ...notification, isRead: true }));
    case NotificationActionEnum.CLEAR_ALL:
      return [];
    default:
      throw new Error(`Unsupported notificationActions type: ${type}`);
  }
};
